import { Component, Input, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { buildRagLog, sentChunkAt } from '../rag-log.util';
import type { RagLogView } from '../rag-log.util';
import { isAiTurn, speakerSides } from '../transcript-timing.util';
import type { RagAudit, SessionReport } from '../tracking-demo.models';

/** What a claim attribution needs to run: the lookup, its answer, and the numbered
 *  chunks the verdict cites into. */
export type RagAuditRequest = {
  callId: number; lookupIndex: number; question: string; answer: string;
  chunks: Array<{ n: number; title: string; subtitle: string; text: string }>;
};

/**
 * The report's RAG tab — every knowledge lookup the agent made during one demo.
 *
 * Reads `live_call_transcripts.qdrant_data`, which stores each Qdrant exchange verbatim
 * precisely because the live path throws the interesting parts away: the point ids and
 * payloads (document_id, original_name, chunk_index) are projected out before any caller
 * sees them, and they are what show WHICH chunks grounded an answer and which documents
 * are duplicated in the collection.
 *
 * All rendering logic lives in rag-log.util (pure, unit-tested); this component only holds
 * the open/closed state of the disclosure rows.
 */
@Component({
  selector: 'app-rag-log',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './rag-log.component.html',
})
export class RagLogComponent {
  private callId: number | null = null;

  /**
   * How to run a claim attribution, supplied by the parent.
   *
   * A function rather than an injected service on purpose: the API service reaches the
   * app's core graph, and pulling that into this component makes it unmountable in a unit
   * test (and drags it into an existing import cycle). The parent already holds the
   * service, so it hands down the one call this needs.
   */
  @Input() auditFn: ((body: RagAuditRequest) => Promise<RagAudit>) | null = null;
  /** The whole report, not just the lookups: pairing each lookup with the answer it
   *  produced needs the transcript, and telling Alex's turns from the prospect's needs the
   *  same speaker resolution the transcript tab uses. */
  @Input({ required: true }) set report(r: SessionReport | null | undefined) {
    const sides = r ? speakerSides(r) : null;
    this.callId = (r?.session as any)?.callId ?? null;
    // The call's agent persona (Alex / Ava / Dena) — every sentence below names it.
    this.agentName.set(((r?.session as any)?.aiAgentName || '').trim() || 'AI');
    this.view.set(buildRagLog(
      r?.ragLog, r?.transcript ?? null,
      sides ? (t: any) => isAiTurn(t, sides) : undefined,
      r?.slides ?? null,
      this.agentName()));
    this.open.set(new Set([0]));   // first lookup expanded, the rest collapsed
    this.rawOpen.set(new Set());
    this.textOpen.set(new Set());
    this.analysing.set(new Set());
    this.auditErrors.set(new Map());
  }

  /** Expand this lookup's disclosure row (the Audit tab's proof links land here). */
  @Input() set focusIndex(i: number | null | undefined) {
    if (i == null) return;
    this.open.update((s) => {
      const next = new Set(s);
      next.add(i);
      return next;
    });
  }

  readonly view = signal<RagLogView>(buildRagLog([]));
  readonly chunkAt = sentChunkAt;
  /** The persona of the agent on this call, for the prose ('AI' until a report lands). */
  readonly agentName = signal('AI');
  private readonly open = signal<Set<number>>(new Set([0]));
  private readonly rawOpen = signal<Set<string>>(new Set());
  private readonly textOpen = signal<Set<string>>(new Set());
  /** Off by default: the scores, filters, timings and point ids answer a question most
   *  readers of this tab are not asking. Hiding them removes nothing. */
  readonly technical = signal(false);
  private readonly analysing = signal<Set<number>>(new Set());
  private readonly auditErrors = signal<Map<number, string>>(new Map());

  isOpen(i: number): boolean { return this.open().has(i); }

  toggleTechnical(): void { this.technical.set(!this.technical()); }

  toggle(i: number): void {
    const next = new Set(this.open());
    next.has(i) ? next.delete(i) : next.add(i);
    this.open.set(next);
  }

  expandAll(): void {
    this.open.set(new Set(this.view().rows.map((_, i) => i)));
  }

  collapseAll(): void {
    this.open.set(new Set());
  }

  isRawOpen(key: string): boolean { return this.rawOpen().has(key); }

  toggleRaw(key: string): void {
    const next = new Set(this.rawOpen());
    next.has(key) ? next.delete(key) : next.add(key);
    this.rawOpen.set(next);
  }

  isTextOpen(key: string): boolean { return this.textOpen().has(key); }

  toggleText(key: string): void {
    const next = new Set(this.textOpen());
    next.has(key) ? next.delete(key) : next.add(key);
    this.textOpen.set(next);
  }

  isAnalysing(i: number): boolean { return this.analysing().has(i); }

  auditError(i: number): string | null { return this.auditErrors().get(i) ?? null; }

  /** Whether this lookup has enough recorded to be worth auditing at all. */
  canAnalyse(i: number): boolean {
    const row = this.view().rows[i];
    return !!(this.auditFn && this.callId && row?.answer?.text && row.sentChunks.length
      && !this.isAnalysing(i));
  }

  /**
   * Attribute this lookup's answer back to its chunks.
   *
   * Spends a model call, so it is never automatic. The chunks and the answer being
   * displayed are what get audited, so the verdict is of exactly what is on screen.
   */
  async analyse(i: number): Promise<void> {
    const row = this.view().rows[i];
    if (!row || !this.auditFn || !this.callId || this.isAnalysing(i)) return;
    this.setAnalysing(i, true);
    this.setAuditError(i, null);
    try {
      const audit = await this.auditFn({
        callId: this.callId,
        lookupIndex: i,
        question: row.query,
        answer: row.answer?.text || '',
        chunks: row.sentChunks,
      });
      const rows = [...this.view().rows];
      rows[i] = { ...rows[i], audit };
      this.view.set({ ...this.view(), rows });
    } catch {
      this.setAuditError(i, 'Could not analyse this lookup. Try again.');
    } finally {
      this.setAnalysing(i, false);
    }
  }

  private setAnalysing(i: number, on: boolean): void {
    const next = new Set(this.analysing());
    on ? next.add(i) : next.delete(i);
    this.analysing.set(next);
  }

  private setAuditError(i: number, message: string | null): void {
    const next = new Map(this.auditErrors());
    message ? next.set(i, message) : next.delete(i);
    this.auditErrors.set(next);
  }

  /** Colour for a claim's verdict — a contradiction is the one to look at first. */
  verdictTone(verdict: string): string {
    if (verdict === 'supported') return 'text-emerald-600 dark:text-emerald-300';
    if (verdict === 'contradicted') return 'text-red-500';
    return 'text-amber-600 dark:text-amber-300';
  }

  verdictLabel(verdict: string): string {
    if (verdict === 'supported') return 'from the chunks';
    if (verdict === 'contradicted') return 'contradicts the chunks';
    return 'not in the chunks';
  }

  /** Qdrant answers the deck-index searches in well under a millisecond, so sub-ms timings
   *  keep a decimal instead of all collapsing to "0ms". */
  msText(ms: number | null): string {
    if (ms == null) return '—';
    return ms < 10 ? `${ms.toFixed(1)}ms` : `${Math.round(ms)}ms`;
  }

  /** Cosine similarity, coloured by how strong the match is. */
  scoreTone(pct: number): string {
    if (pct >= 70) return 'bg-emerald-500';
    if (pct >= 60) return 'bg-amber-400';
    return 'bg-slate-300 dark:bg-slate-500';
  }
}
